import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import TuneIcon from '@mui/icons-material/Tune';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import {
  GEO_SCORE_RUBRIC,
  type Catalogue,
  type CatalogueProduct,
  type CatalogueCategory,
  type OptimizeRun,
  type GeoScoreDimension,
} from '@traaker/shared';

type OptimizeItem = CatalogueProduct | CatalogueCategory;
import { WafWhitelistDialog } from './WafWhitelistDialog';
import { WordingEditorDialog } from './WordingEditorDialog';
import { CustomSectionsDialog } from './CustomSectionsDialog';
import { CatalogueIcon } from './CatalogueIcon';
import { formatDate } from '../../i18n/format';
import { bucketScoreHistory, bucketLabel, mergeBucketColumns } from './scoreHistoryBuckets';

/**
 * Product module — Product Optimization page (Phase 2). Pick a catalogue, run the
 * async optimize (fetch + GEO-score + wording rewrite) with a live % bar, browse
 * items worst-score-first, edit wording, surface WAF-blocked pages, export CSV.
 *
 * Scale: browse is SERVER-paginated (worst-first over the geoscore-index GSI) with
 * a score-band filter, a category-subtree filter, and semantic search — so a 50k
 * catalogue never ships to the browser. Products + categories are separate tabs,
 * each optimizable independently.
 */

type ItemType = 'product' | 'category';
type Band = 'all' | 'critical' | 'improve' | 'good' | 'unscored';

const BANDS: { id: Band; label: string; color: 'default' | 'error' | 'warning' | 'success' }[] = [
  { id: 'all', label: 'Tous', color: 'default' },
  { id: 'critical', label: 'Critiques (<40)', color: 'error' },
  { id: 'improve', label: 'À améliorer (40-69)', color: 'warning' },
  { id: 'good', label: 'Bons (70+)', color: 'success' },
  { id: 'unscored', label: 'Non analysés', color: 'default' },
];

const PAGE_SIZE = 50;

/** "Why it matters" per dimension — frontend-only UI copy (NOT shipped into the
 *  model prompt). Explains, in plain terms, why each criterion drives AI citation,
 *  grounded in the GEO research corpus. Rendered under the rubric in the column
 *  info tooltip. */
const DIM_WHY: Record<GeoScoreDimension, string> = {
  structuredData:
    "Le JSON-LD Schema.org est le signal que Google/Gemini exploitent le plus directement : il laisse un moteur IA lire prix, dispo et avis sans deviner.",
  factualDensity:
    "Les moteurs IA citent des faits, pas du marketing. Des chiffres précis (dimensions, matériaux) donnent des éléments repris tels quels dans la réponse.",
  specificity:
    "Une affirmation vague n'est jamais citée. Le concret et le vérifiable sont ce qu'un moteur reprend en confiance.",
  shopperIntent:
    "C'est le filtre d'entrée : la page doit ressembler à la vraie requête multi-critères de l'acheteur pour être remontée par la recherche du moteur.",
  extractability:
    "Restructurer une page (titres, listes, tableaux, paragraphes autoportants) augmente à elle seule le taux de citation d'environ +17 %, sans changer le fond.",
  authorityEvidence:
    "Preuves réelles (notes, avis, labels) + angle comparatif face à des alternatives nommées : c'est le levier le plus fort sur le classement d'un moteur IA. Jamais de preuve inventée.",
  qaCoverage:
    "Un bloc Q/R fournit des réponses prêtes à l'emploi qu'un moteur IA peut lever directement pour répondre à l'acheteur.",
};

/** Score-band background/foreground, matching BrandAxisPage's matrix cells. */
function scoreCellColor(score: number | undefined): { bg: string; fg: string } {
  if (score == null) return { bg: 'transparent', fg: '#D1D5DB' };
  if (score >= 70) return { bg: '#DCFCE7', fg: '#15803D' };
  if (score >= 40) return { bg: '#FEF3C7', fg: '#92400E' };
  return { bg: '#FEE2E2', fg: '#991B1B' };
}

/** Rich column-header tooltip: label · what (rubric) · why it matters. */
function DimInfo({ dim }: { dim: GeoScoreDimension }) {
  return (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {GEO_SCORE_RUBRIC[dim].label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
        <strong>Comment c'est noté :</strong> {GEO_SCORE_RUBRIC[dim].rubric}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        <strong>Pourquoi ça compte :</strong> {DIM_WHY[dim]}
      </Typography>
    </Box>
  );
}

interface Stats {
  critical: number;
  improve: number;
  good: number;
  scored: number;
  total: number;
}

/** Professional score-distribution header: one stacked bar + per-band tallies. */
function ScoreDistribution({
  stats,
  band,
  onBand,
}: {
  stats: Stats;
  band: Band;
  onBand: (b: Band) => void;
}) {
  const total = Math.max(stats.total, 1);
  const unscored = Math.max(stats.total - stats.scored, 0);
  const segs = [
    { id: 'critical' as Band, n: stats.critical, color: '#d32f2f', label: 'Critiques' },
    { id: 'improve' as Band, n: stats.improve, color: '#ed6c02', label: 'À améliorer' },
    { id: 'good' as Band, n: stats.good, color: '#2e7d32', label: 'Bons' },
    { id: 'unscored' as Band, n: unscored, color: '#c4c4c4', label: 'Non analysés' },
  ];
  return (
    <Paper data-tour="product-opt-distribution" variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Répartition des scores GEO</Typography>
        <Typography variant="caption" color="text.secondary">
          {stats.scored.toLocaleString()} / {stats.total.toLocaleString()} analysés
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          height: 12,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'action.hover',
        }}
      >
        {segs.map(
          (s) =>
            s.n > 0 && (
              <Tooltip key={s.id} title={`${s.label} : ${s.n.toLocaleString()}`}>
                <Box sx={{ width: `${(s.n / total) * 100}%`, bgcolor: s.color }} />
              </Tooltip>
            ),
        )}
      </Box>
      <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        {segs.map((s) => (
          <Chip
            key={s.id}
            size="small"
            variant={band === s.id ? 'filled' : 'outlined'}
            onClick={() => onBand(band === s.id ? 'all' : s.id)}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                <span>
                  {s.label} · <strong>{s.n.toLocaleString()}</strong>
                </span>
              </Stack>
            }
          />
        ))}
      </Stack>
    </Paper>
  );
}

export default function ProductOptimization() {
  const { activeBrand } = useBrand();
  const { show } = useToast();
  const brandId = activeBrand?.brandId;

  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [catalogueId, setCatalogueId] = useState<string>('');
  const [type, setType] = useState<ItemType>('product');
  const [band, setBand] = useState<Band>('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [items, setItems] = useState<OptimizeItem[]>([]);
  const [cursors, setCursors] = useState<string[]>([]); // cursor stack for prev
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState<Stats | null>(null);
  const [run, setRun] = useState<OptimizeRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [wafOpen, setWafOpen] = useState(false);
  const [wafSample, setWafSample] = useState<{ count: number; urls: string[] }>({ count: 0, urls: [] });
  const [editItem, setEditItem] = useState<OptimizeItem | null>(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [stratRecs, setStratRecs] = useState<Record<string, { title: string; detail: string }>>({});
  const [stratOnly, setStratOnly] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const catalogue = catalogues.find((c) => c.catalogueId === catalogueId);

  // Load catalogues for the brand.
  useEffect(() => {
    if (!brandId) return;
    retailerCatalogApi.list(brandId).then((cs) => {
      setCatalogues(cs);
      if (cs.length && !catalogueId) setCatalogueId(cs[0].catalogueId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Switching catalogue is a hard reset: clear run/items/filters SYNCHRONOUSLY so
  // a stale "Analyse terminée" from the previous catalogue never lingers.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    setRun(null);
    setStats(null);
    setStratRecs({});
    setStratOnly(false);
    setItems([]);
    setCursors([]);
    setNextCursor(undefined);
    setPageIndex(0);
    setBand('all');
    setQuery('');
    setDebouncedQuery('');
    setType('product');
  }, [catalogueId]);

  // Debounce the search box.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(h);
  }, [query]);

  // Load a page of items (server-paginated worst-first, or semantic search).
  const loadItems = useCallback(
    async (cursor?: string) => {
      if (!brandId || !catalogueId) return;
      setLoading(true);
      try {
        const res = await retailerCatalogApi.optimizeItems(brandId, catalogueId, {
          type,
          band: debouncedQuery ? undefined : band,
          q: debouncedQuery || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        setItems(res.items as OptimizeItem[]);
        setNextCursor(res.nextCursor);
      } catch (e: any) {
        show(e?.message || 'Erreur de chargement', { severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [brandId, catalogueId, type, band, debouncedQuery, show],
  );

  // Reset to page 1 whenever the filter/type/search changes.
  useEffect(() => {
    setCursors([]);
    setPageIndex(0);
    loadItems();
  }, [loadItems]);

  // Load stats + latest run + strategic recs on catalogue/type change.
  useEffect(() => {
    if (!brandId || !catalogueId) return;
    retailerCatalogApi.optimizeStats(brandId, catalogueId, type).then(setStats).catch(() => setStats(null));
    retailerCatalogApi.optimizeProgress(brandId, catalogueId).then((r) => setRun(r)).catch(() => undefined);
    retailerCatalogApi
      .strategicRecs(brandId, catalogueId)
      .then((recs) => {
        const map: Record<string, { title: string; detail: string }> = {};
        for (const r of recs) map[r.productId] = { title: r.title, detail: r.detail };
        setStratRecs(map);
      })
      .catch(() => setStratRecs({}));
  }, [brandId, catalogueId, type]);

  // Poll while a run is active; refresh items + stats on completion.
  useEffect(() => {
    if (!brandId || !catalogueId || run?.status !== 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      const r = await retailerCatalogApi.optimizeProgress(brandId, catalogueId, run.runId);
      setRun(r);
      if (r && r.status !== 'running') {
        if (pollRef.current) clearInterval(pollRef.current);
        loadItems();
        retailerCatalogApi.optimizeStats(brandId, catalogueId, type).then(setStats).catch(() => undefined);
        if (r.blocked > 0) {
          setWafSample(await retailerCatalogApi.optimizeBlocked(brandId, catalogueId));
        }
      }
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [brandId, catalogueId, type, run?.status, run?.runId, loadItems]);

  const startRun = async () => {
    if (!brandId || !catalogueId) return;
    setStarting(true);
    try {
      const r = await retailerCatalogApi.startOptimize(brandId, catalogueId, {
        onlyProducts: type === 'product',
        onlyCategories: type === 'category',
      });
      setRun(r);
      show('Optimisation lancée', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Lancement impossible', { severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const doExport = async () => {
    if (!brandId || !catalogueId) return;
    try {
      await retailerCatalogApi.downloadCsv(brandId, catalogueId);
    } catch (e: any) {
      show(e?.message || 'Export impossible', { severity: 'error' });
    }
  };

  const typeLabel = type === 'product' ? 'produits' : 'catégories';
  const visible = useMemo(
    () => items.filter((it) => !stratOnly || stratRecs[it.productId]),
    [items, stratOnly, stratRecs],
  );
  const progressPct =
    run && run.total > 0
      ? Math.round(
          ((run.completed + run.blocked + run.failed + (run.gone ?? 0)) / run.total) * 100,
        )
      : 0;
  const dims = Object.keys(GEO_SCORE_RUBRIC) as GeoScoreDimension[];
  const stratCount = Object.keys(stratRecs).length;

  // History columns (BP-matrix style, oldest→newest left-to-right), coarsened
  // per scoreHistoryBuckets.ts: daily for the last 7 days, weekly for the
  // next 3 weeks, quarterly (capped to 3) for the next year, yearly beyond
  // that. Storage keeps full daily granularity for 60 days (the retention
  // cascade) — this only groups it for display. Excludes each item's own
  // most recent `scoreHistory` entry — that entry is always the current run
  // (the worker's last write, same value as `geoScore`), which the dedicated
  // "Score GEO" column already shows; `geoScore` stays the single source of
  // truth for the current score since `scoreHistory` can be absent entirely
  // on items scored before this feature shipped.
  const bucketedByItem = useMemo(() => {
    const now = new Date();
    const map = new Map<string, ReturnType<typeof bucketScoreHistory>>();
    for (const it of visible) {
      const prior = [...(it.scoreHistory || [])].sort((a, b) => (a.t < b.t ? -1 : 1)).slice(0, -1);
      map.set(it.productId, bucketScoreHistory(prior, now));
    }
    return map;
  }, [visible]);

  const historyColumns = useMemo(
    () => mergeBucketColumns([...bucketedByItem.values()]),
    [bucketedByItem],
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!brandId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Sélectionnez une marque.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">Optimisation catalogue</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl data-tour="product-opt-catalogue" size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Catalogue</InputLabel>
          <Select label="Catalogue" value={catalogueId} onChange={(e) => setCatalogueId(e.target.value)}>
            {catalogues.map((c) => (
              <MenuItem key={c.catalogueId} value={c.catalogueId}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CatalogueIcon icon={c.icon} sx={{ fontSize: 18 }} />
                  <span>
                    {c.name} ({type === 'category' ? c.totalCategories : c.totalProducts})
                  </span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          data-tour="product-opt-run"
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={startRun}
          disabled={!catalogueId || starting || run?.status === 'running'}
        >
          Analyser {typeLabel}
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={doExport} disabled={!catalogueId}>
          Export CSV
        </Button>
        <Button
          data-tour="product-opt-sections"
          startIcon={<TuneIcon />}
          onClick={() => setSectionsOpen(true)}
          disabled={!catalogueId}
        >
          Configurer
        </Button>
      </Stack>

      {catalogues.length === 0 && (
        <Alert severity="info">Créez d'abord un catalogue dans l'onglet Catalogue.</Alert>
      )}

      {catalogues.length > 0 && (
        <Tabs
          data-tour="product-opt-types"
          value={type}
          onChange={(_, v) => setType(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="product"
            label={`Produits${catalogue ? ` (${catalogue.totalProducts.toLocaleString()})` : ''}`}
          />
          <Tab
            value="category"
            label={`Catégories${catalogue ? ` (${catalogue.totalCategories.toLocaleString()})` : ''}`}
          />
        </Tabs>
      )}

      {stats && stats.total > 0 && <ScoreDistribution stats={stats} band={band} onBand={setBand} />}

      {stratCount > 0 && type === 'product' && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Chip
            color={stratOnly ? 'secondary' : 'default'}
            variant={stratOnly ? 'filled' : 'outlined'}
            label={`${stratCount} recommandation${stratCount > 1 ? 's' : ''} stratégique${stratCount > 1 ? 's' : ''}`}
            onClick={() => setStratOnly((v) => !v)}
          />
          <Typography variant="caption" color="text.secondary">
            Issues de l'analyse concurrentielle (Visibilité GEO) — cliquez pour filtrer.
          </Typography>
        </Stack>
      )}

      {run && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {run.status === 'running'
                  ? `Analyse en cours… ${run.completed + run.blocked + run.failed + (run.gone ?? 0)}/${run.total}`
                  : `Analyse terminée · ${run.completed} réussies · ${run.blocked} bloquées${
                      run.gone ? ` · ${run.gone} pages supprimées du site (404/410)` : ''
                    } · ${run.failed} échecs`}
              </Typography>
              <LinearProgress variant="determinate" value={progressPct} />
            </Box>
            {run.blocked > 0 && (
              <Button
                color="warning"
                size="small"
                startIcon={<WarningAmberIcon />}
                onClick={async () => {
                  if (!wafSample.count) {
                    setWafSample(await retailerCatalogApi.optimizeBlocked(brandId, catalogueId));
                  }
                  setWafOpen(true);
                }}
              >
                {run.blocked} bloquées — voir
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Search + active-filter row */}
      {catalogues.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Recherche sémantique dans les ${typeLabel}…`}
            sx={{ minWidth: 320, flex: 1, maxWidth: 520 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {debouncedQuery && (
            <Typography variant="caption" color="text.secondary">
              Résultats les plus proches — le filtre par score est ignoré pendant la recherche.
            </Typography>
          )}
          {!debouncedQuery && band !== 'all' && (
            <Chip
              size="small"
              label={BANDS.find((b) => b.id === band)?.label}
              color={BANDS.find((b) => b.id === band)?.color}
              onDelete={() => setBand('all')}
            />
          )}
        </Stack>
      )}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">
              {debouncedQuery
                ? 'Aucun résultat pour cette recherche.'
                : band === 'unscored'
                  ? `Aucun${type === 'category' ? 'e' : ''} ${type === 'category' ? 'catégorie' : 'produit'} en attente d'analyse.`
                  : `Aucun${type === 'category' ? 'e' : ''} élément.`}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '38%' }}>{type === 'category' ? 'Catégorie' : 'Produit'}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
                    <span>Score GEO</span>
                    <Tooltip
                      title={`À quel point un moteur IA (ChatGPT, Gemini…) peut comprendre et recommander chaque ${
                        type === 'category' ? 'catégorie' : 'produit'
                      } à partir de sa page — noté sur 7 critères. Cliquez sur une ligne pour le détail.`}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
                {historyColumns.map((col) => {
                  const raw = bucketLabel(col, new Date());
                  const label =
                    col.tier === 'daily' || col.tier === 'weekly'
                      ? formatDate(raw, { day: '2-digit', month: 'short' })
                      : raw;
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{ fontSize: '0.72rem', color: 'text.secondary', minWidth: 64 }}
                    >
                      {label}
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                    <span>Réécriture GEO</span>
                    <Tooltip title="Une proposition de fiche optimisée (arguments, description, specs chiffrées, usages, FAQ) que vous pouvez copier sur votre site pour améliorer le score. Cliquez sur une ligne pour la voir et la copier.">
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((it) => {
                const href = it.urlNormalized.startsWith('http')
                  ? it.urlNormalized
                  : `https://${it.urlNormalized}`;
                const isOpen = expandedId === it.productId;
                const itemBuckets = bucketedByItem.get(it.productId) || [];
                return (
                  <>
                    <TableRow
                      key={it.productId}
                      hover
                      sx={{ cursor: 'pointer', bgcolor: isOpen ? 'action.selected' : undefined }}
                      onClick={() => setExpandedId(isOpen ? null : it.productId)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Box sx={{ maxWidth: 300, overflow: 'hidden' }}>
                            <Typography
                              variant="body2"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {it.name || it.urlNormalized}
                            </Typography>
                            {it.extractedTitle && it.extractedTitle !== it.name && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {it.extractedTitle}
                              </Typography>
                            )}
                          </Box>
                          {stratRecs[it.productId] && (
                            <Tooltip title={`${stratRecs[it.productId].title} — ${stratRecs[it.productId].detail}`}>
                              <Chip size="small" color="secondary" label="stratégique" sx={{ height: 18 }} />
                            </Tooltip>
                          )}
                          <Tooltip title="Ouvrir la page">
                            <IconButton
                              size="small"
                              component="a"
                              href={href}
                              target="_blank"
                              rel="noopener"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <OpenInNewIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {it.geoScore != null ? (
                          (() => {
                            const colors = scoreCellColor(it.geoScore);
                            return (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: 40,
                                  height: 26,
                                  px: 1,
                                  borderRadius: 1,
                                  bgcolor: colors.bg,
                                  color: colors.fg,
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {it.geoScore}
                              </Box>
                            );
                          })()
                        ) : it.fetchStatus === 'blocked' ? (
                          <Chip size="small" label="bloqué" color="warning" variant="outlined" />
                        ) : it.fetchStatus === 'gone' ? (
                          <Tooltip title="Page supprimée du site (404/410). À retirer du catalogue — un ré-audit ne la récupérera pas.">
                            <Chip size="small" label="supprimée" color="default" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      {historyColumns.map((col) => {
                        const point = itemBuckets.find((b) => b.key === col.key);
                        if (!point) {
                          return (
                            <TableCell key={col.key} align="center" sx={{ color: '#D1D5DB' }}>
                              —
                            </TableCell>
                          );
                        }
                        const colors = scoreCellColor(point.s);
                        return (
                          <TableCell key={col.key} align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 36,
                                height: 24,
                                px: 0.75,
                                borderRadius: 1,
                                bgcolor: colors.bg,
                                color: colors.fg,
                                fontWeight: 600,
                                fontSize: '0.78rem',
                              }}
                            >
                              {point.s}
                            </Box>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Tooltip title={it.wording ? 'Voir / copier la réécriture GEO' : "Lancez l'analyse pour générer la réécriture"}>
                          <span>
                            <Button
                              size="small"
                              startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditItem(it);
                              }}
                              disabled={!it.wording}
                            >
                              Voir
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={historyColumns.length + 3} sx={{ p: 0, bgcolor: 'action.hover' }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}>
                              Détail du dernier score{it.wordingUpdatedAt ? ` — ${formatDate(it.wordingUpdatedAt, { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                            </Typography>
                            {it.geoScore == null ? (
                              <Typography variant="body2" color="text.secondary">
                                Pas encore analysé.
                              </Typography>
                            ) : (
                              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                {dims.map((d) => {
                                  const score = it.dimensionScores?.[d];
                                  const colors = scoreCellColor(score);
                                  return (
                                    <Box key={d} sx={{ minWidth: 140 }}>
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Typography variant="caption" fontWeight={600}>
                                          {GEO_SCORE_RUBRIC[d].label}
                                        </Typography>
                                        <Tooltip title={<DimInfo dim={d} />}>
                                          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
                                        </Tooltip>
                                      </Stack>
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          minWidth: 40,
                                          height: 26,
                                          px: 1,
                                          mt: 0.5,
                                          borderRadius: 1,
                                          bgcolor: colors.bg,
                                          color: colors.fg,
                                          fontWeight: 700,
                                          fontSize: '0.85rem',
                                        }}
                                      >
                                        {score ?? '—'}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Pagination — disabled during semantic search (single relevance-ranked page). */}
      {!debouncedQuery && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {visible.length > 0
              ? `${(pageIndex * PAGE_SIZE + 1).toLocaleString()}–${(pageIndex * PAGE_SIZE + visible.length).toLocaleString()} · page ${pageIndex + 1}`
              : '—'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            disabled={pageIndex === 0 || loading}
            onClick={() => {
              setCursors([]);
              setPageIndex(0);
              loadItems();
            }}
          >
            Début
          </Button>
          <Button
            size="small"
            disabled={pageIndex === 0 || loading}
            onClick={() => {
              const prev = [...cursors];
              prev.pop();
              const target = prev[prev.length - 1];
              setCursors(prev);
              setPageIndex((i) => Math.max(0, i - 1));
              loadItems(target);
            }}
          >
            Précédent
          </Button>
          <Button
            size="small"
            disabled={!nextCursor || loading}
            onClick={() => {
              setCursors((c) => [...c, nextCursor!]);
              setPageIndex((i) => i + 1);
              loadItems(nextCursor);
            }}
          >
            Suivant
          </Button>
        </Stack>
      )}

      <WafWhitelistDialog
        open={wafOpen}
        blockedCount={run?.blocked ?? wafSample.count}
        sampleUrls={wafSample.urls}
        onClose={() => setWafOpen(false)}
      />
      {catalogue && (
        <CustomSectionsDialog
          open={sectionsOpen}
          initial={catalogue.customSections || []}
          initialDisabledSections={catalogue.disabledSections}
          onClose={() => setSectionsOpen(false)}
          onSave={async (sections, disabledSections) => {
            const updated = await retailerCatalogApi.updateCustomSections(
              brandId,
              catalogueId,
              sections,
              disabledSections,
            );
            setCatalogues((cs) => cs.map((c) => (c.catalogueId === catalogueId ? updated : c)));
            setSectionsOpen(false);
            show('Configuration enregistrée', { severity: 'success' });
          }}
        />
      )}
      {editItem && (
        <WordingEditorDialog
          open
          item={editItem}
          customSections={catalogue?.customSections}
          disabledSections={catalogue?.disabledSections}
          onClose={() => setEditItem(null)}
          onSave={async (wording) => {
            await retailerCatalogApi.updateWording(brandId, catalogueId, skOf(editItem), wording);
            show('Wording enregistré', { severity: 'success' });
            setEditItem(null);
            loadItems(cursors[cursors.length - 1]);
          }}
        />
      )}
    </Box>
  );
}

/** Items from listItems don't carry SK (stripped) — reconstruct it for the update. */
function skOf(it: OptimizeItem): string {
  return it.type === 'category'
    ? `CATEGORY#${it.categoryPath}`
    : `PRODUCT#${it.categoryPath}#${it.productId}`;
}
